import { CountryItem } from '@digiseg-labs/api-client';
import { CountryFlag } from '@digiseg-labs/design-system';
import { CaretDownIcon, DesktopIcon, GlobeIcon, HomeIcon, MagnifyingGlassIcon, PersonIcon, ViewHorizontalIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Flex, Select, Text, TextField } from '@radix-ui/themes';
import { ReactNode, useMemo, useState } from 'react';
import { formatLargeNumber } from '../../utils/number';


type CountryListProps = {
  countries: CountryItem[];
  selectedCountry: CountryItem | undefined;
  onCountryChange: (country: CountryItem | undefined) => void;
};

const formatStat = (value?: number, locale?: string) =>
  typeof value === 'number' ? formatLargeNumber(value, locale) : '–';

const StatBadge = ({
  icon,
  label,
  color,
}: {
  icon: ReactNode;
  label: string;
  color: 'green' | 'blue' | 'orange';
}) => (
  <Badge
    color={color}
    variant="soft"
    size="2"
    className="country-stat-badge"
  >
    {icon}
    {label}
  </Badge>
);

export const CountryStatBadges = ({ stats }: { stats?: CountryItem['reach_stats'] }) => {
  const locale = useMemo(() => (typeof navigator !== 'undefined' ? navigator.language : undefined), []);

  return (
    <Flex gap="2" wrap="wrap" justify="center" className="country-stat-badges">
      <StatBadge icon={<HomeIcon width="16" height="16" />} label={`${formatStat(stats?.num_households, locale)} households`} color="green" />
      <StatBadge icon={<PersonIcon width="16" height="16" />} label={`${formatStat(stats?.num_persons, locale)} people`} color="blue" />
      <StatBadge icon={<DesktopIcon width="16" height="16" />} label={`${formatStat(stats?.num_devices, locale)} devices`} color="orange" />
    </Flex>
  );
};

const CountryRow = ({
  country,
  isSelected,
  onSelect,
}: {
  country: CountryItem | undefined;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <Button
      type="button"
      variant="ghost"
      color="gray"
      onClick={onSelect}
      className={`country-row-button${isSelected ? ' is-selected' : ''}`}
    >
      <Flex align="center" justify="between" gap="3" className="w-100">
        <Flex align="center" gap="3" className="min-width-0">
          {country ? (
            <CountryFlag code={country.code} size={22} shape="square" />
          ) : (
            <Box className="country-flag">
              <GlobeIcon width="18" height="18" />
            </Box>
          )}
          <Box className="min-width-0">
            <Text size="3" weight="medium" className="text-block">
              {country ? country.display_name : 'All countries'}
            </Text>
          </Box>
        </Flex>
        <CountryStatBadges stats={country?.reach_stats} />
      </Flex>
    </Button>
  );
};

export const CountryList = ({ countries, selectedCountry, onCountryChange }: CountryListProps) => {
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();
  const shouldFilter = normalized.length >= 2;

  const filtered = useMemo(() => {
    if (!shouldFilter) return countries;
    return countries.filter((country) => {
      const name = country.display_name.toLowerCase();
      const code = country.code.toLowerCase();
      return name.includes(normalized) || code.includes(normalized);
    });
  }, [countries, normalized, shouldFilter]);

  return (
    <Flex direction="column" gap="2">
      <TextField.Root
        placeholder="Search countries..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        size="3"
        className="fullwidth"
      >
        <TextField.Slot side="left">
          <MagnifyingGlassIcon />
        </TextField.Slot>
      </TextField.Root>
      {filtered.map((country) => (
        <CountryRow
          key={country.code}
          country={country}
          isSelected={country.code === selectedCountry?.code}
          onSelect={() => onCountryChange(country)}
        />
      ))}
      {shouldFilter && filtered.length === 0 && (
        <Text color="gray" size="2">
          No countries found for “{query}”.
        </Text>
      )}
    </Flex>
  );
};

export default CountryList;
